// Copyright (c) 2021 WiseTime. All rights reserved.

import { EMPTY, Observable, of } from "rxjs"
import { Action, UnknownAction } from "redux"
import { catchError, debounceTime, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as ToolbarActions from "../actions"
import * as ToolbarSlice from "../slice"
import { SearchTerms } from "../slice"
import type { RootState } from "../../../../app/store"
import { getCurrentDate, getWeekRange, isFutureDate, toDate } from "../../dateTime"
import { getMergedFilter, isSearchNotEmpty } from "../../search-bar/searchUtils"
import { showSnackBar } from "../../../snackbar/actions"
import { executor, Init, timelineService } from "../../../../app/rpc"
import {
  CountFilteredTimeRowsRequestSchema,
  CountFilteredTimeRowsResponse,
} from "../../../../generated/wisetime/user/timeline/v4/timeline_pb"

type CountFilteredTimeRowsRpc = (request: Init<typeof CountFilteredTimeRowsRequestSchema>) => Observable<CountFilteredTimeRowsResponse>

const createRequest = (searchTerms: SearchTerms, state: RootState): Observable<Init<typeof CountFilteredTimeRowsRequestSchema>> => {
  const week = getWeekRange(toDate(state.timeline.activeDate))
  if (isFutureDate(week.startDateLocal) || !state.timeline.timeZone) {
    return EMPTY
  }

  const filter = getMergedFilter(searchTerms, state.userContext.currentSiteId)
  return of({
    startDate: week.startDateLocal.toString(),
    endDate: isFutureDate(week.endDateLocal) ? getCurrentDate().toString() : week.endDateLocal.toString(),
    filter: {
      activities: filter.activities,
      descriptions: filter.descriptions,
      tags: filter.tags.map(tag => tag.id),
    },
  })
}

const isActionMatching = (action: UnknownAction): boolean => {
  // count filtered time rows on filter change
  return ToolbarSlice.addSearchActivity.match(action) ||
    ToolbarSlice.addSearchDescription.match(action) ||
    ToolbarSlice.addSearchTag.match(action) ||
    ToolbarSlice.removeSearchTerm.match(action) ||
    ToolbarSlice.addBroadSearch.match(action) ||
    ToolbarSlice.addTermsSearch.match(action) ||
    // count filtered time rows on demand
    ToolbarActions.countFilteredTimeRowsByDay.match(action)
}

export const countFilteredTimeRowsByDay = (countFilteredTimeRowsRpc: CountFilteredTimeRowsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(action => isActionMatching(action)),
      debounceTime(200),
      withLatestFrom(state$),
      mergeMap(([, state]) => of(state.toolbar.searchTerms).pipe(
        filter(isSearchNotEmpty),
        mergeMap(filter => createRequest(filter, state)),
        mergeMap(countFilteredTimeRowsRpc),
        map(t => t),
        map(ToolbarSlice.setSearchMatches),
        catchError(handleError)),
      )
    )

const handleError = (): Observable<Action> =>
  of(showSnackBar({
    message: t({ id: "msg_count_filtered_time_rows_failed", message: "Failed to count search matches. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))

const countFilteredTimeRowsRpc = (request: Init<typeof CountFilteredTimeRowsRequestSchema>): Observable<CountFilteredTimeRowsResponse> =>
  executor(() => timelineService.countFilteredTimeRows(create(CountFilteredTimeRowsRequestSchema, request)))

export default countFilteredTimeRowsByDay(countFilteredTimeRowsRpc)
